﻿package com.mgacreative.hotelru.domain.repository

import com.mgacreative.hotelru.core.network.SupabaseNetworkClient
import com.mgacreative.hotelru.domain.model.PricePeriod
import io.github.jan.supabase.postgrest.postgrest
import kotlinx.datetime.*

class PricingRepository(private val networkClient: SupabaseNetworkClient) {

    suspend fun getAllPricePeriods(): List<PricePeriod> {
        return try {
            networkClient.client.postgrest["price_periods"]
                .select()
                .decodeList<PricePeriod>()
        } catch (e: Exception) {
            e.printStackTrace()
            networkClient.handleError(e)
            emptyList()
        }
    }

    suspend fun getPricePeriods(roomType: String): List<PricePeriod> {
        return try {
            networkClient.client.postgrest["price_periods"]
                .select {
                    filter {
                        eq("room_type", roomType)
                    }
                }
                .decodeList<PricePeriod>()
        } catch (e: Exception) {
            e.printStackTrace()
            networkClient.handleError(e)
            emptyList()
        }
    }

    suspend fun addPricePeriod(pricePeriod: PricePeriod): PricePeriod? {
        return try {
            networkClient.client.postgrest["price_periods"]
                .insert(pricePeriod) {
                    select()
                }
                .decodeSingleOrNull<PricePeriod>()
        } catch (e: Exception) {
            e.printStackTrace()
            networkClient.handleError(e)
            null
        }
    }

    suspend fun updatePricePeriod(id: String, name: String, start: String, end: String, price: Double): Boolean {
        return try {
            networkClient.client.postgrest["price_periods"]
                .update({
                    set("name", name)
                    set("start_date", start)
                    set("end_date", end)
                    set("price", price)
                }) {
                    filter {
                        eq("id", id)
                    }
                }
            true
        } catch (e: Exception) {
            e.printStackTrace()
            networkClient.handleError(e)
            false
        }
    }

    suspend fun deletePricePeriod(id: String): Boolean {
        return try {
            networkClient.client.postgrest["price_periods"]
                .delete {
                    filter {
                        eq("id", id)
                    }
                }
            true
        } catch (e: Exception) {
            e.printStackTrace()
            networkClient.handleError(e)
            false
        }
    }

    // Calculates the total price for a given date range using periods (falling back to a base price if needed)
    suspend fun calculateTotalPrice(roomTypeId: String, start: LocalDate, end: LocalDate, basePrice: Double): Double {
        val periods = getPricePeriods(roomTypeId)
        var total = 0.0
        var currentDate = start

        while (currentDate < end) {
            // Find if current date falls into any period
            // If multiple periods match, we take the one with the highest priority (e.g. latest created or highest price)
            // For now, we just pick the first matching period
            val matchingPeriod = periods.firstOrNull {
                val periodStart = LocalDate.parse(it.startDate)
                val periodEnd = LocalDate.parse(it.endDate)
                currentDate >= periodStart && currentDate <= periodEnd
            }

            total += matchingPeriod?.price ?: basePrice
            currentDate = currentDate.plus(1, DateTimeUnit.DAY)
        }

        return total
    }
}

